import { ChangeDetectionStrategy, Component, OnInit, inject, input, signal } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import {
  TeaserSectionData,
  UpdateTeaserSectionPayload,
} from '@app/utils/interfaces/teaser.interface';
import { EditorBodyAdapter } from '@app/utils/interfaces/editor-interface';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-teaser-editor-body',
  standalone: true,
  imports: [ReactiveFormsModule, TranslateModule],
  templateUrl: './teaser-editor-body.component.html',
  styleUrl: './teaser-editor-body.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TeaserEditorBodyComponent
  implements OnInit, EditorBodyAdapter<UpdateTeaserSectionPayload>
{
  public readonly data = input.required<TeaserSectionData>();
  public readonly isPreviewMode = input(false);

  private readonly fb = inject(FormBuilder);

  private selectedTeaserImageFile: File | null = null;
  protected readonly teaserImagePreview = signal<string>('');

  protected readonly form = this.fb.nonNullable.group({
    isVisible: [true],
    heading: ['', [Validators.required, Validators.maxLength(255)]],
    additionalText: ['', [Validators.maxLength(1000)]],
    link: ['', [Validators.maxLength(2048)]],
    teaserImageUrl: ['', [Validators.maxLength(5000)]],
    headingAlignment: [0, [Validators.required]],
    additionalTextAlignment: [0, [Validators.required]],
    headingColor: ['#242426', [Validators.required]],
    additionalTextColor: ['#242426', [Validators.required]],
  });

  public readonly alignmentOptions = [
    { label: 'Left align', value: 0 },
    { label: 'Center align', value: 1 },
    { label: 'Right align', value: 2 },
  ];

  public ngOnInit(): void {
    const data = this.data();

    this.form.patchValue({
      isVisible: data.isVisible,
      heading: data.heading ?? '',
      additionalText: data.additionalText ?? '',
      link: data.link ?? '',
      teaserImageUrl: data.teaserImageUrl ?? '',
      headingAlignment: data.headingAlignment ?? 0,
      additionalTextAlignment: data.additionalTextAlignment ?? 0,
      headingColor: data.headingColor || '#242426',
      additionalTextColor: data.additionalTextColor || '#242426',
    });

    this.teaserImagePreview.set(data.teaserImageUrl ?? '');
  }

  protected onFileSelected(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const file = inputElement.files?.[0];

    if (!file?.type.startsWith('image/')) {
      return;
    }

    this.selectedTeaserImageFile = file;

    const reader = new FileReader();

    reader.onload = () => {
      const preview = reader.result as string;

      this.teaserImagePreview.set(preview);
      this.form.controls.teaserImageUrl.setValue(preview);
    };

    reader.readAsDataURL(file);
  }

  public isValid(): boolean {
    return this.form.valid;
  }

  public markAllAsTouched(): void {
    this.form.markAllAsTouched();
  }

  public hasChanges(): boolean {
    const value = this.form.getRawValue();
    const data = this.data();

    return (
      JSON.stringify({
        isVisible: value.isVisible,
        heading: value.heading,
        additionalText: value.additionalText,
        link: value.link,
        teaserImageUrl: value.teaserImageUrl,
        headingAlignment: value.headingAlignment,
        additionalTextAlignment: value.additionalTextAlignment,
        headingColor: value.headingColor,
        additionalTextColor: value.additionalTextColor,
        hasNewImage: Boolean(this.selectedTeaserImageFile),
      }) !==
      JSON.stringify({
        isVisible: data.isVisible,
        heading: data.heading ?? '',
        additionalText: data.additionalText ?? '',
        link: data.link ?? '',
        teaserImageUrl: data.teaserImageUrl ?? '',
        headingAlignment: data.headingAlignment ?? 0,
        additionalTextAlignment: data.additionalTextAlignment ?? 0,
        headingColor: data.headingColor || '#242426',
        additionalTextColor: data.additionalTextColor || '#242426',
        hasNewImage: false,
      })
    );
  }

  public canPreview(): boolean {
    const value = this.form.getRawValue();

    return Boolean(
      value.heading.trim() ||
      value.additionalText.trim() ||
      this.teaserImagePreview() ||
      value.headingColor.trim() ||
      value.additionalTextColor.trim(),
    );
  }

  public getVisibilityValue(): boolean {
    return this.form.controls.isVisible.value;
  }

  public setVisibilityValue(value: boolean): void {
    this.form.controls.isVisible.setValue(value);
  }

  public getSubmitPayload(): UpdateTeaserSectionPayload {
    const value = this.form.getRawValue();

    return {
      isVisible: value.isVisible,
      heading: value.heading.trim(),
      additionalText: value.additionalText.trim(),
      link: value.link.trim(),
      teaserImage: this.selectedTeaserImageFile,
      headingAlignment: Number(value.headingAlignment),
      additionalTextAlignment: Number(value.additionalTextAlignment),
      headingColor: value.headingColor,
      additionalTextColor: value.additionalTextColor,
    };
  }

  protected get previewHeadingAlignmentClass(): string {
    return this.getAlignmentClass(this.form.controls.headingAlignment.value);
  }

  protected get previewTextAlignmentClass(): string {
    return this.getAlignmentClass(this.form.controls.additionalTextAlignment.value);
  }

  protected get previewImageUrl(): string {
    return this.teaserImagePreview() || this.form.controls.teaserImageUrl.value;
  }

  private getAlignmentClass(value: number): string {
    switch (value) {
      case 1:
        return 'is-center';
      case 2:
        return 'is-right';
      default:
        return 'is-left';
    }
  }
}
